import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock, History, Briefcase, Activity, MapPin, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import SearchInput from "@/components/dashboard/SearchInput";
import { StatusPill, safeNum, orDash } from "@/components/dashboard/Primitives";
import { deleteEmployee, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function EmployeeDetailModal({ employee, allJobs = [], allJobDetails = {}, open, onClose, onDeleted }) {
    const { user } = useAuth();
    const isPrivileged = user && (user.role === "owner" || user.role === "admin");
    const [jobSearch, setJobSearch] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [deleting, setDeleting] = useState(false);

    const employeeJobs = useMemo(() => {
        if (!employee) return [];
        return allJobs.filter((j) => {
            const det = allJobDetails[j.id];
            if (det?.employees_log?.length) return det.employees_log.some((l) => l.employee === employee.name);
            return j.employee === employee.name.split(" ")[0];
        });
    }, [employee, allJobs, allJobDetails]);

    const filteredJobs = useMemo(() => {
        const q = jobSearch.toLowerCase().trim();
        if (!q) return employeeJobs;
        return employeeJobs.filter((j) => [j.id, j.customer, j.task, j.status, j.address].some((v) => (v || "").toString().toLowerCase().includes(q)));
    }, [employeeJobs, jobSearch]);

    if (!employee) return null;
    const activeJobs = filteredJobs.filter((j) => !["Afsluttet"].includes(j.status));
    const pastJobs = filteredJobs.filter((j) => ["Afsluttet"].includes(j.status));

    // Aggregate signup history across jobs
    const signupHistory = [];
    employeeJobs.forEach((j) => {
        const det = allJobDetails[j.id];
        (det?.employees_log || []).filter((l) => l.employee === employee.name).forEach((l) =>
            signupHistory.push({ job_id: j.id, ...l })
        );
    });

    const h = employee.hours || {};
    // Resolve location from current task
    const currentJob = employeeJobs.find((j) => j.id === employee.current_task);
    const currentLocation = currentJob?.address || "Ingen aktiv lokation";

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        {employee.name}
                        <StatusPill status={employee.status} />
                    </DialogTitle>
                </DialogHeader>

                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-slate-50 rounded-xl p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Rolle</p>
                        <p className="font-semibold text-slate-900">{orDash(employee.role)}</p>
                        <p className="text-xs text-slate-500">{orDash(employee.team)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Aktuel opgave</p>
                        <p className="font-semibold text-slate-900">{employee.current_task || "—"}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{currentLocation}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-sm">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Seneste aktivitet</p>
                        <p className="font-semibold text-slate-900">{orDash(employee.last_seen)}</p>
                    </div>
                </div>

                <section className="mt-5">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-slate-400" />Timer</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        {[
                            { l: "I dag", v: h.day },
                            { l: "Denne uge", v: h.week },
                            { l: "Denne måned", v: h.month },
                            { l: "I år", v: h.year },
                            { l: "Total", v: safeNum(h.year) + safeNum(h.month) },
                        ].map((x) => (
                            <div key={x.l} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400">{x.l}</p>
                                <p className="text-xl font-bold text-slate-900 mt-1">{safeNum(x.v)}t</p>
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-3 text-center">
                            <p className="text-[10px] uppercase tracking-wider text-amber-700">Sygedage (år)</p>
                            <p className="text-xl font-bold text-slate-900 mt-1">{safeNum(h.sick_days)}</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 text-center">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Andet fravær (år)</p>
                            <p className="text-xl font-bold text-slate-900 mt-1">{safeNum(h.absence_days)}</p>
                        </div>
                    </div>
                </section>

                <section className="mt-5">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Briefcase className="w-4 h-4 text-slate-400" />Jobs ({employeeJobs.length})</h3>
                        <SearchInput value={jobSearch} onChange={setJobSearch} placeholder="Søg jobs…" testid="employee-job-search" />
                    </div>
                    <div className="space-y-3">
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1">Aktuelle ({activeJobs.length})</p>
                            {activeJobs.length === 0 ? (
                                <p className="text-sm text-slate-400 italic">Ingen aktive jobs.</p>
                            ) : (
                                <ul className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100 max-h-48 overflow-y-auto">
                                    {activeJobs.map((j) => (
                                        <li key={j.id} className="px-3 py-2 flex items-center justify-between text-sm">
                                            <div><p className="font-mono text-slate-500">{j.id}</p><p className="text-slate-800">{j.customer || "Ukendt"} — {j.task || "—"}</p></div>
                                            <StatusPill status={j.status} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Afsluttede ({pastJobs.length})</p>
                            {pastJobs.length === 0 ? (
                                <p className="text-sm text-slate-400 italic">Ingen tidligere jobs.</p>
                            ) : (
                                <ul className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100 max-h-48 overflow-y-auto">
                                    {pastJobs.map((j) => (
                                        <li key={j.id} className="px-3 py-2 flex items-center justify-between text-sm">
                                            <div><p className="font-mono text-slate-500">{j.id}</p><p className="text-slate-800">{j.customer || "Ukendt"} — {j.task || "—"}</p></div>
                                            <StatusPill status={j.status} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </section>

                <section className="mt-5">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><History className="w-4 h-4 text-slate-400" />Til/frameldt historik</h3>
                    {signupHistory.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">Ingen registreringer.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100 max-h-48 overflow-y-auto">
                            {signupHistory.map((s, i) => (
                                <li key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                                    <span className="font-mono text-slate-500">{s.job_id}</span>
                                    <span><span className={`inline-block mr-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.action === "frameldt" ? "bg-red-50 text-red-700" : s.action === "tilmeldt" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>{s.action}</span><span className="text-slate-500">{s.time}</span></span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <div className="mt-6 flex justify-between items-center">
                    {isPrivileged ? (
                        <Button
                            data-testid="employee-detail-delete"
                            variant="outline"
                            onClick={() => { setReason(""); setConfirmOpen(true); }}
                            className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                        >
                            <Trash2 className="w-4 h-4 mr-1.5" />
                            Slet medarbejder
                        </Button>
                    ) : <span />}
                    <Button data-testid="employee-detail-close" onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white">Luk</Button>
                </div>
            </DialogContent>

            {/* Confirmation dialog — requires a reason */}
            <Dialog open={confirmOpen} onOpenChange={(o) => !o && !deleting && setConfirmOpen(false)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="w-5 h-5" />
                            Slet {employee.name}?
                        </DialogTitle>
                    </DialogHeader>
                    <div className="mt-3 space-y-3">
                        <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-900">
                            Handlingen kan ikke fortrydes. Medarbejder-profilen fjernes permanent, og et audit-spor gemmes med årsagen nedenfor.
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-700">Årsag <span className="text-red-600">*</span></label>
                            <textarea
                                data-testid="employee-delete-reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                rows={3}
                                maxLength={500}
                                autoFocus
                                placeholder="Fx: Ophør af ansættelse pr. 31.03.2026 — anmodning fra medarbejder"
                                className="mt-1.5 w-full rounded-md border border-slate-200 p-2 text-sm focus:border-red-300 focus:outline-none focus:ring-1 focus:ring-red-200"
                            />
                            <div className="mt-1 text-[11px] text-slate-500 flex justify-between">
                                <span>Min. 3 tegn · max 500 tegn</span>
                                <span>{reason.length}/500</span>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setConfirmOpen(false)}
                                disabled={deleting}
                                data-testid="employee-delete-cancel"
                            >Annullér</Button>
                            <Button
                                type="button"
                                data-testid="employee-delete-confirm"
                                disabled={deleting || reason.trim().length < 3}
                                onClick={async () => {
                                    setDeleting(true);
                                    try {
                                        await deleteEmployee(employee.id, reason.trim());
                                        toast.success("Medarbejder slettet");
                                        setConfirmOpen(false);
                                        if (onDeleted) onDeleted(employee);
                                        onClose();
                                    } catch (err) {
                                        toast.error(formatApiError(err));
                                    } finally {
                                        setDeleting(false);
                                    }
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                                Slet permanent
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
